import { useTranslation } from 'react-i18next';
import ReactTags from 'react-tag-autocomplete';
import { BombiixChip } from '../BombiixChip';

/**
 * One tag, as react-tag-autocomplete v6 models both a selection and a suggestion.
 * Both fields are optional: a tag typed under `allowNew` reaches `onAddition`
 * with only the name the user entered.
 */
export interface BombiixTag {
  id?: string | number;
  name?: string;
}

/**
 * Props react-tag-autocomplete v6 hands its `tagComponent` (`ReactTags.esm.js:411`).
 * `onDelete` is declared without parameters so it can be passed straight to
 * `BombiixChip`; React still forwards the click event to it, which the library's
 * `onDeleteTag` reads for focus restoration.
 */
interface TagChipProps {
  tag: BombiixTag;
  onDelete: () => void;
}

/**
 * Renders one selected tag as the master-design chip in place of the library's
 * own `.react-tags__selected-tag` button, styled by `.bombiix-chip--tag` to match
 * the Product table view's tag badge. Module scope keeps its element type
 * stable across renders, so chips are not unmounted while the user is typing.
 */
function TagChip({ tag, onDelete }: TagChipProps) {
  const { t } = useTranslation('product-information');
  const name = tag.name ?? '';
  return (
    <BombiixChip variant="green" className="bombiix-chip--tag" removeLabel={t('TAGS.REMOVE_TAG', { tag: name })} onRemove={onDelete}>
      {name}
    </BombiixChip>
  );
}

/**
 * `.react-tags__selected` is kept so every per-screen layout rule still matches;
 * the chip row reuses the master-design flex/gap already defined in `_chip.scss`.
 */
const SELECTED_CLASS_NAMES = { selected: 'react-tags__selected bombiix-chip-select__chips' };

export interface BombiixTagsInputProps {
  tags: BombiixTag[];
  suggestions: BombiixTag[];
  onAddition: (_tag: BombiixTag) => void;
  onDelete: (_index: number) => void;
  placeholderText: string;
  onFocus?: () => void;
  allowNew?: boolean;
  delimiters?: string[];
}

/**
 * BombiixTagsInput — the app's tag field. Wraps react-tag-autocomplete so every
 * Tags screen renders master-design chips from one place, rather than each one
 * reskinning the library's pill in its own stylesheet.
 */
function BombiixTagsInput(props: BombiixTagsInputProps) {
  return <ReactTags {...props} tagComponent={TagChip} classNames={SELECTED_CLASS_NAMES} />;
}

export default BombiixTagsInput;
